"use client"

import { useEffect, useCallback } from "react"
import { usePathname, useSearchParams } from "next/navigation"

import { getCurrentStage } from "../utils/participant"
import { useAppSelector } from "@/lib/hooks"

export const GOOGLE_ANALYTICS_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID
export const ENV = process.env.NEXT_PUBLIC_ENV

type GtagCommand = "event" | "config" | "set"
const callGtag = (command: GtagCommand, target: string, params?: Record<string, unknown>): void => {
  if (typeof window === "undefined" || typeof gtag !== "function") {
    return
  }
  ;(gtag as (cmd: GtagCommand, t: string, p?: Record<string, unknown>) => void)(command, target, params)
}

export const sendEvent = (name: string, parameters?: Record<string, unknown>) => {
  if (typeof window === "undefined") return
  callGtag("event", name, {
    page_location: window.location.pathname,
    page_path: window.location.pathname,
    ...parameters,
  })
}

export const GoogleAnalyticsConfig = (): null => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const uuid = searchParams.get("uuid")
  const user = useAppSelector((state) => state.auth?.user)
  const ciab = useAppSelector((state) => state.configuration?.ciabName)
  const stage = user ? getCurrentStage(user) : undefined
  const queryParams = new URLSearchParams(window.location.search)
  const googleAnalyticsId = queryParams.get("ga_user_id") || user?.googleAnalyticsId
  const researchStage = queryParams.get("research_stage") || stage?.name
  const cohort = user?.cohort?.name

  const configureGoogleAnalytics4 = useCallback(() => {
    if (!GOOGLE_ANALYTICS_ID) return
    callGtag("config", GOOGLE_ANALYTICS_ID, {
      send_page_view: false,
      cookie_flags: "secure;samesite=none",
      user_id: googleAnalyticsId,
      research_stage: researchStage,
      research_stage_year: stage?.index,
      ciab,
      cohort,
      env: ENV,
    })
    callGtag("set", "user_properties", {
      cohort,
      ciab,
      user_id_dimension: googleAnalyticsId,
    })
  }, [googleAnalyticsId, researchStage, cohort, ciab, stage])

  const sendPageView = useCallback(() => {
    sendEvent("page_view", {
      page_location: pathname,
      page_path: pathname,
      ...(uuid && { uuid }),
    })
  }, [pathname, uuid])

  useEffect(configureGoogleAnalytics4, [configureGoogleAnalytics4])
  useEffect(sendPageView, [sendPageView])

  return null
}

export default GoogleAnalyticsConfig
